// product-stock-validation.js
(function () {
  if (typeof document === 'undefined') return;

  /**
   * Check if inventory is tracked for the selected variant
   */
  function isInventoryTracked(form) {
    const variantSelect = form?.querySelector('select[name="id"]');
    if (variantSelect) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      const inventoryManagement = selectedOption?.getAttribute('data-inventory-management');
      // If inventory_management is null or empty string, inventory is not tracked
      return inventoryManagement !== null && inventoryManagement !== '';
    }
    return false;
  }

  /**
   * Get inventory quantity from quantity input or variant select
   * Returns null if inventory is not tracked
   */
  function getInventoryQuantity(form) {
    // If inventory is not tracked, return null to skip validation
    if (!isInventoryTracked(form)) {
      return null;
    }

    const normalizeQty = window.utils?.normalizeInventoryQuantity || ((q) => Math.max(0, q || 0));

    const quantityInput = form?.querySelector('ck-quantity-input');
    if (quantityInput) {
      const inventoryAttr = quantityInput.getAttribute('data-inventory-quantity');
      if (inventoryAttr !== null) {
        const qty = normalizeQty(inventoryAttr);
        return qty;
      }
    }

    // Fallback: get from variant select option
    const variantSelect = form?.querySelector('select[name="id"]');
    if (variantSelect) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      const inventoryAttr = selectedOption?.dataset?.inventoryQuantity;
      if (inventoryAttr !== undefined) {
        const qty = normalizeQty(inventoryAttr);
        return qty;
      }
    }

    return null;
  }

  /**
   * Get current quantity from quantity input
   */
  function getCurrentQuantity(form) {
    const quantityInput = form?.querySelector('ck-quantity-input');
    if (!quantityInput) return 1;

    const innerInput = quantityInput.querySelector('input');
    if (innerInput && innerInput.value != null) {
      const n = parseInt(innerInput.value, 10);
      return isNaN(n) || n < 1 ? 1 : n;
    }

    const raw = (quantityInput.value ?? quantityInput.getAttribute('value') ?? '1') + '';
    const n = parseInt(raw, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  /**
   * Update stock validation UI
   */
  function updateStockValidation(form) {
    if (!form) return;

    const quantityInput = form.querySelector('ck-quantity-input');
    const addToCartButton = form.querySelector('[data-add-to-cart-button]');
    const errorMessage = form.querySelector('[data-stock-error-message]');
    const stockMessage = form.querySelector('p:has(span.font-semibold)');

    if (!quantityInput) return;

    const inventoryQty = getInventoryQuantity(form);
    const currentQty = getCurrentQuantity(form);

    // If no inventory data, skip validation
    if (inventoryQty === null) {
      if (addToCartButton) {
        addToCartButton.disabled = false;
        addToCartButton.removeAttribute('aria-describedby');
      }
      if (errorMessage) {
        errorMessage.classList.add('hidden');
        errorMessage.textContent = '';
      }
      return;
    }

    const isOutOfStock = inventoryQty === 0;
    const exceedsStock = currentQty > inventoryQty;

    // Update quantity input max and disabled state
    if (isOutOfStock) {
      quantityInput.disabled = true;
      quantityInput.setAttribute('max', '0');
    } else {
      quantityInput.setAttribute('max', String(inventoryQty));
      // Only disable if it was already disabled due to availability
      // (we don't want to re-enable if product is unavailable)
      const variantSelect = form.querySelector('select[name="id"]');
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const availableAttr = selectedOption?.getAttribute('data-available');
        const isAvailable = availableAttr === 'true' || availableAttr === '';
        if (isAvailable) {
          quantityInput.disabled = false;
        }
      }
    }

    // Update add to cart button
    if (addToCartButton) {
      if (isOutOfStock || exceedsStock) {
        addToCartButton.disabled = true;
        if (exceedsStock) {
          addToCartButton.setAttribute('aria-describedby', 'stock-error-message');
        }
      } else {
        addToCartButton.disabled = false;
        addToCartButton.removeAttribute('aria-describedby');
      }
    }

    // Update error message
    if (errorMessage) {
      if (exceedsStock && !isOutOfStock) {
        const message = `Only ${inventoryQty} ${inventoryQty === 1 ? 'item is' : 'items are'} available. Please reduce the quantity.`;
        errorMessage.textContent = message;
        errorMessage.classList.remove('hidden');
        errorMessage.id = 'stock-error-message';
      } else {
        errorMessage.classList.add('hidden');
        errorMessage.textContent = '';
        errorMessage.removeAttribute('id');
      }
    }

    // Update stock message if quantity exceeds
    // Use inline style with CSS variable instead of classList (CSS variable is not a Tailwind class)
    if (stockMessage && exceedsStock && !isOutOfStock) {
      stockMessage.style.color = 'var(--color-accent-secondary)';
    } else if (stockMessage) {
      stockMessage.style.color = '';
    }
  }

  /**
   * Handle variant change - update quantity input max and revalidate
   */
  function handleVariantChange(event) {
    const form = event.target.closest('form');
    if (!form) return;

    const variantSelect = form.querySelector('select[name="id"]');
    if (!variantSelect || event.target !== variantSelect) return;

    const selectedOption = variantSelect.options[variantSelect.selectedIndex];
    if (!selectedOption) return;

    const quantityInput = form.querySelector('ck-quantity-input');
    if (!quantityInput) return;

    // Check if inventory is tracked
    const inventoryManagement = selectedOption.getAttribute('data-inventory-management');
    const isTracked = inventoryManagement !== null && inventoryManagement !== '';

    const inventoryQtyAttr = selectedOption.getAttribute('data-inventory-quantity');
    const inventoryQty = inventoryQtyAttr !== null ? parseInt(inventoryQtyAttr, 10) : null;
    const availableAttr = selectedOption.getAttribute('data-available');
    const isAvailable = availableAttr === 'true' || availableAttr === '';

    // Update quantity input - only set max and data-inventory-quantity if inventory is tracked
    if (isTracked && inventoryQty !== null) {
      quantityInput.setAttribute('data-inventory-quantity', String(inventoryQty));
      quantityInput.setAttribute('max', String(inventoryQty));
    } else {
      // Remove inventory restrictions if inventory is not tracked
      quantityInput.removeAttribute('data-inventory-quantity');
      quantityInput.removeAttribute('max');
    }

    // Update disabled state
    if (!isAvailable) {
      quantityInput.disabled = true;
    } else {
      quantityInput.disabled = false;
      // Reset quantity to 1 if current quantity exceeds new inventory (only if tracked)
      const currentQty = getCurrentQuantity(form);
      if (isTracked && inventoryQty !== null && currentQty > inventoryQty) {
        quantityInput.value = Math.min(currentQty, inventoryQty);
      }
    }

    // Revalidate
    updateStockValidation(form);
  }

  /**
   * Handle quantity change - validate against inventory
   */
  function handleQuantityChange(event) {
    const form = event.target.closest('form');
    if (!form) return;

    // Small delay to ensure value is updated
    setTimeout(() => {
      updateStockValidation(form);
    }, 0);
  }

  /**
   * Initialize stock validation
   */
  function init() {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    if (!forms.length) return;

    forms.forEach((form) => {
      if (form.hasAttribute('data-stock-validation-bound')) return;
      form.setAttribute('data-stock-validation-bound', 'true');

      const quantityInput = form.querySelector('ck-quantity-input');
      const variantSelect = form.querySelector('select[name="id"]');

      // Listen to quantity changes
      if (quantityInput) {
        quantityInput.addEventListener('quantity-change', handleQuantityChange);
        quantityInput.addEventListener('input', handleQuantityChange);
        quantityInput.addEventListener('change', handleQuantityChange);
      }

      // Listen to variant changes
      if (variantSelect) {
        variantSelect.addEventListener('change', handleVariantChange);
      }

      // Initial validation
      updateStockValidation(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
